import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { useEquipment } from '../../../context/EquipmentContext';
import { collection, query, where, getDocs, orderBy, doc as firestoreDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import RepairCompletionModal from './RepairCompletionModal';
import { uploadFile } from '../../../utils/storageUtils';
import { compressImage } from '../../../utils/imageUtils';

const MyRepairRequestsPage = () => {
  const navigate = useNavigate();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const { receiveRepair, requestRepairCompletion } = useEquipment();
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [receiveCondition, setReceiveCondition] = useState('GOOD');
  const [receiveDescription, setReceiveDescription] = useState('');
  const [receiveImages, setReceiveImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedCompletionRepair, setSelectedCompletionRepair] = useState(null);

  useEffect(() => {
    const loadRepairs = async () => {
      if (!currentUser || !currentTeam) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const repairsQuery = query(
          collection(db, 'equipmentRepairs'),
          where('teamId', '==', currentTeam.id),
          where('reportedBy', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(repairsQuery);
        const repairsData = await Promise.all(
          snapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            const equipmentRef = firestoreDoc(db, 'equipment', data.equipmentId);
            const equipmentSnap = await getDoc(equipmentRef);
            const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;

            return {
              id: docSnapshot.id,
              ...data,
              equipment: equipmentData
            };
          })
        );

        setRepairs(repairsData);
      } catch (err) {
        console.error('Error loading repairs:', err);
        // Fallback: query without orderBy
        try {
          const fallbackQuery = query(
            collection(db, 'equipmentRepairs'),
            where('teamId', '==', currentTeam.id),
            where('reportedBy', '==', currentUser.uid)
          );
          const snapshot = await getDocs(fallbackQuery);
          const repairsData = await Promise.all(
            snapshot.docs.map(async (docSnapshot) => {
              const data = docSnapshot.data();
              const equipmentRef = firestoreDoc(db, 'equipment', data.equipmentId);
              const equipmentSnap = await getDoc(equipmentRef);
              const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;

              return {
                id: docSnapshot.id,
                ...data,
                equipment: equipmentData
              };
            })
          );
          // Sort in memory
          repairsData.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA;
          });
          setRepairs(repairsData);
        } catch (fallbackErr) {
          console.error('Error loading repairs (fallback):', fallbackErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadRepairs();
  }, [currentUser, currentTeam]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (repair) => {
    const status = repair.status || repair;
    const receiveStatus = repair.receiveStatus;
    
    // If repair is completed, check receive status
    if (status === 'COMPLETED' || (typeof repair === 'object' && repair.status === 'COMPLETED')) {
      if (receiveStatus === 'PENDING') {
        return { bg: 'bg-blue-100', text: 'text-blue-800', label: '📥 รออนุมัติรับกลับ', icon: '📥' };
      } else if (receiveStatus === 'APPROVED') {
        return { bg: 'bg-green-100', text: 'text-green-800', label: '✅ รับกลับแล้ว', icon: '✅' };
      } else if (receiveStatus === 'REJECTED') {
        return { bg: 'bg-red-100', text: 'text-red-800', label: '❌ รับกลับถูกปฏิเสธ', icon: '❌' };
      }
      return { bg: 'bg-gray-100', text: 'text-gray-800', label: '✅ ซ่อมเสร็จแล้ว', icon: '✅' };
    }
    
    const badges = {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '⏳ รออนุมัติ', icon: '⏳' },
      APPROVED: { bg: 'bg-green-100', text: 'text-green-800', label: '✅ อนุมัติแล้ว', icon: '✅' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-800', label: '❌ ถูกปฏิเสธ', icon: '❌' },
      IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-800', label: '🔧 กำลังซ่อม', icon: '🔧' },
      COMPLETED: { bg: 'bg-gray-100', text: 'text-gray-800', label: '✅ ซ่อมเสร็จแล้ว', icon: '✅' }
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: '📋' };
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setUploadingImages(true);
      const uploadedUrls = [];

      for (const file of files) {
        const compressedFile = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.8
        });

        const fileName = `equipment-receive-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        const path = `equipment/${currentTeam.id}/${fileName}`;
        const url = await uploadFile(compressedFile, path);
        uploadedUrls.push(url);
      }

      setReceiveImages([...receiveImages, ...uploadedUrls]);
    } catch (err) {
      console.error('Error uploading images:', err);
      alert('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setReceiveImages(receiveImages.filter((_, i) => i !== index));
  };

  const handleReceiveRepair = async () => {
    if (!selectedRepair) return;

    try {
      setIsSubmitting(true);
      const result = await receiveRepair(selectedRepair.id, {
        condition: receiveCondition,
        description: receiveDescription.trim(),
        images: receiveImages
      });

      if (result.success) {
        alert('ส่งคำขอรับกลับอุปกรณ์สำเร็จ รอการอนุมัติจากหัวหน้าทีม');
        setShowReceiveModal(false);
        setSelectedRepair(null);
        setReceiveCondition('GOOD');
        setReceiveDescription('');
        setReceiveImages([]);
        // Reload repairs
        window.location.reload();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error receiving repair:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group by status
  const statusGroups = {
    pending: repairs.filter(r => r.status === 'PENDING'),
    approved: repairs.filter(r => r.status === 'APPROVED'),
    inProgress: repairs.filter(r => r.status === 'IN_PROGRESS'),
    completed: repairs.filter(r => r.status === 'COMPLETED'),
    rejected: repairs.filter(r => r.status === 'REJECTED')
  };

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <Link
            to="/nurse/teams"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/nurse/equipment"
              className="text-gray-600 hover:text-gray-800"
            >
              ← กลับ
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">อุปกรณ์ที่ฉันส่งซ่อม</h1>
          </div>
          <Link
            to="/nurse/equipment/repair"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + ส่งซ่อมใหม่
          </Link>
        </div>

        {/* Status Groups */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* ⏳ รออนุมัติ */}
            {statusGroups.pending.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  ⏳ รออนุมัติ ({statusGroups.pending.length})
                </h2>
                <div className="space-y-4">
                  {statusGroups.pending.map(repair => {
                    const statusBadge = getStatusBadge(repair);
                    return (
                      <div
                        key={repair.id}
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {repair.equipment && (
                                <>
                                  <span className="text-2xl">🩺</span>
                                  <div>
                                    <h3 className="font-semibold text-gray-800">{repair.equipment.name || repair.equipmentName}</h3>
                                    <p className="text-sm text-gray-600">{repair.equipment.code || repair.equipmentCode}</p>
                                  </div>
                                </>
                              )}
                              <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <p className="text-xs text-gray-500">📅 ส่งคำขอ</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {formatDateTime(repair.createdAt)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">⚡ ความเร่งด่วน</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {repair.urgency === 'URGENT' ? '🔴 เร่งด่วน' : '🟢 ปกติ'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">📝 ปัญหา</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {repair.problemType === 'NOT_WORKING' ? 'ไม่ทำงาน' :
                                   repair.problemType === 'DAMAGED' ? 'เสียหาย' :
                                   repair.problemType === 'MAINTENANCE' ? 'บำรุงรักษา' : 'อื่นๆ'}
                                </p>
                              </div>
                            </div>

                            {repair.problemDescription && (
                              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">รายละเอียด:</p>
                                <p className="text-sm text-gray-700">{repair.problemDescription}</p>
                              </div>
                            )}

                            {repair.images && repair.images.length > 0 && (
                              <div className="mt-4">
                                <p className="text-xs text-gray-500 mb-2">รูปภาพ:</p>
                                <div className="grid grid-cols-3 gap-2">
                                  {repair.images.map((img, idx) => (
                                    <img
                                      key={idx}
                                      src={img}
                                      alt={`Repair ${idx + 1}`}
                                      className="w-full h-24 object-cover rounded-lg"
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="ml-4 flex flex-col gap-2">
                            <Link
                              to={`/nurse/equipment/repair/${repair.id}`}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                            >
                              ✏️ แก้ไข
                            </Link>
                            {repair.equipment && (
                              <Link
                                to={`/nurse/equipment/${repair.equipmentId}`}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium text-center"
                              >
                                👁️ ดูรายละเอียด
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ✅ อนุมัติแล้ว */}
            {statusGroups.approved.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  ✅ อนุมัติแล้ว ({statusGroups.approved.length})
                </h2>
                <div className="space-y-4">
                  {statusGroups.approved.map(repair => {
                    const statusBadge = getStatusBadge(repair);
                    return (
                      <div
                        key={repair.id}
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {repair.equipment && (
                                <>
                                  <span className="text-2xl">🩺</span>
                                  <div>
                                    <h3 className="font-semibold text-gray-800">{repair.equipment.name || repair.equipmentName}</h3>
                                    <p className="text-sm text-gray-600">{repair.equipment.code || repair.equipmentCode}</p>
                                  </div>
                                </>
                              )}
                              <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <p className="text-xs text-gray-500">✅ อนุมัติเมื่อ</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {formatDateTime(repair.approvedAt)}
                                </p>
                                {repair.approvedByName && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    โดย: {repair.approvedByName}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="ml-4 flex flex-col gap-2">
                            {repair.completionRequestStatus === 'PENDING' ? (
                              <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium text-center">
                                ⏳ รออนุมัติซ่อมเสร็จ
                              </span>
                            ) : (
                              <button
                                onClick={() => {
                                  setSelectedCompletionRepair(repair);
                                  setShowCompletionModal(true);
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                              >
                                ✅ ซ่อมเสร็จแล้ว
                              </button>
                            )}
                            {repair.equipment && (
                              <Link
                                to={`/nurse/equipment/${repair.equipmentId}`}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                              >
                                👁️ ดูรายละเอียด
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 🔧 กำลังซ่อม */}
            {statusGroups.inProgress.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  🔧 กำลังซ่อม ({statusGroups.inProgress.length})
                </h2>
                <div className="space-y-4">
                  {statusGroups.inProgress.map(repair => {
                    const statusBadge = getStatusBadge(repair);
                    return (
                      <div
                        key={repair.id}
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {repair.equipment && (
                                <>
                                  <span className="text-2xl">🩺</span>
                                  <div>
                                    <h3 className="font-semibold text-gray-800">{repair.equipment.name || repair.equipmentName}</h3>
                                    <p className="text-sm text-gray-600">{repair.equipment.code || repair.equipmentCode}</p>
                                  </div>
                                </>
                              )}
                              <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                            </div>
                          </div>

                          <div className="ml-4 flex flex-col gap-2">
                            {repair.completionRequestStatus === 'PENDING' ? (
                              <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium text-center">
                                ⏳ รออนุมัติซ่อมเสร็จ
                              </span>
                            ) : (
                              <button
                                onClick={() => {
                                  setSelectedCompletionRepair(repair);
                                  setShowCompletionModal(true);
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                              >
                                ✅ ซ่อมเสร็จแล้ว
                              </button>
                            )}
                            {repair.equipment && (
                              <Link
                                to={`/nurse/equipment/${repair.equipmentId}`}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                              >
                                👁️ ดูรายละเอียด
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ✅ ซ่อมเสร็จแล้ว */}
            {statusGroups.completed.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  ✅ ซ่อมเสร็จแล้ว ({statusGroups.completed.length})
                </h2>
                <div className="space-y-4">
                  {statusGroups.completed.map(repair => {
                    const statusBadge = getStatusBadge(repair);
                    return (
                      <div
                        key={repair.id}
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {repair.equipment && (
                                <>
                                  <span className="text-2xl">🩺</span>
                                  <div>
                                    <h3 className="font-semibold text-gray-800">{repair.equipment.name || repair.equipmentName}</h3>
                                    <p className="text-sm text-gray-600">{repair.equipment.code || repair.equipmentCode}</p>
                                  </div>
                                </>
                              )}
                              <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                            </div>

                            {repair.completedAt && (
                              <div className="mt-4">
                                <p className="text-xs text-gray-500">✅ ซ่อมเสร็จเมื่อ</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {formatDateTime(repair.completedAt)}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="ml-4 flex flex-col gap-2">
                            {!repair.receiveStatus && (
                              <button
                                onClick={() => {
                                  setSelectedRepair(repair);
                                  setShowReceiveModal(true);
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                              >
                                📥 รับกลับอุปกรณ์
                              </button>
                            )}
                            {repair.receiveStatus === 'PENDING' && (
                              <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium text-center">
                                ⏳ รออนุมัติรับกลับ
                              </span>
                            )}
                            {repair.receiveStatus === 'APPROVED' && (
                              <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium text-center">
                                ✅ รับกลับแล้ว
                              </span>
                            )}
                            {repair.receiveStatus === 'REJECTED' && (
                              <>
                                <span className="px-4 py-2 bg-red-100 text-red-800 rounded-lg text-sm font-medium text-center mb-2">
                                  ❌ รับกลับถูกปฏิเสธ
                                </span>
                                {repair.receiveRejectionReason && (
                                  <p className="text-xs text-red-600 text-center">
                                    {repair.receiveRejectionReason}
                                  </p>
                                )}
                                <button
                                  onClick={() => {
                                    setSelectedRepair(repair);
                                    setShowReceiveModal(true);
                                  }}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                                >
                                  📥 รับกลับใหม่
                                </button>
                              </>
                            )}
                            {repair.equipment && (
                              <Link
                                to={`/nurse/equipment/${repair.equipmentId}`}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                              >
                                👁️ ดูรายละเอียด
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ❌ ถูกปฏิเสธ */}
            {statusGroups.rejected.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  ❌ ถูกปฏิเสธ ({statusGroups.rejected.length})
                </h2>
                <div className="space-y-4">
                  {statusGroups.rejected.map(repair => {
                    const statusBadge = getStatusBadge(repair);
                    return (
                      <div
                        key={repair.id}
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {repair.equipment && (
                                <>
                                  <span className="text-2xl">🩺</span>
                                  <div>
                                    <h3 className="font-semibold text-gray-800">{repair.equipment.name || repair.equipmentName}</h3>
                                    <p className="text-sm text-gray-600">{repair.equipment.code || repair.equipmentCode}</p>
                                  </div>
                                </>
                              )}
                              <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                            </div>

                            {repair.rejectedAt && (
                              <div className="mt-4">
                                <p className="text-xs text-gray-500">❌ ปฏิเสธเมื่อ</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {formatDateTime(repair.rejectedAt)}
                                </p>
                                {repair.rejectionReason && (
                                  <p className="text-xs text-red-600 mt-1">
                                    เหตุผล: {repair.rejectionReason}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="ml-4 flex flex-col gap-2">
                            {repair.equipment && (
                              <Link
                                to={`/nurse/equipment/${repair.equipmentId}`}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                              >
                                👁️ ดูรายละเอียด
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {repairs.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <div className="text-6xl mb-4">🔧</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">ไม่มีรายการ</h3>
                <p className="text-gray-600 mb-4">คุณยังไม่มีการส่งซ่อมอุปกรณ์</p>
                <Link
                  to="/nurse/equipment/repair"
                  className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  + ส่งซ่อมใหม่
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Receive Repair Modal */}
      {showReceiveModal && selectedRepair && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">รับกลับอุปกรณ์จากการซ่อม</h3>
            
            {/* Equipment Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🩺</span>
                <div>
                  <h4 className="font-semibold text-gray-800">{selectedRepair.equipment?.name || selectedRepair.equipmentName}</h4>
                  <p className="text-sm text-gray-600">{selectedRepair.equipment?.code || selectedRepair.equipmentCode}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Condition */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สภาพอุปกรณ์ <span className="text-red-500">*</span>
                </label>
                <select
                  value={receiveCondition}
                  onChange={(e) => setReceiveCondition(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="GOOD">✅ ดี (พร้อมใช้งาน)</option>
                  <option value="DAMAGED">⚠️ เสียหาย</option>
                  <option value="NEEDS_REPAIR">🔧 ต้องซ่อมอีก</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รายละเอียดเพิ่มเติม
                </label>
                <textarea
                  value={receiveDescription}
                  onChange={(e) => setReceiveDescription(e.target.value)}
                  rows={4}
                  placeholder="ระบุรายละเอียดสภาพอุปกรณ์..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รูปภาพ (ถ้ามี)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploadingImages}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                {receiveImages.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {receiveImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={img}
                          alt={`Receive ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => handleRemoveImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowReceiveModal(false);
                  setSelectedRepair(null);
                  setReceiveCondition('GOOD');
                  setReceiveDescription('');
                  setReceiveImages([]);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleReceiveRepair}
                disabled={isSubmitting || uploadingImages}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isSubmitting ? 'กำลังส่ง...' : 'ส่งคำขอรับกลับ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completion Request Modal */}
      <RepairCompletionModal
        isOpen={showCompletionModal}
        onClose={() => {
          setShowCompletionModal(false);
          setSelectedCompletionRepair(null);
        }}
        repair={selectedCompletionRepair}
        equipmentData={selectedCompletionRepair?.equipment}
        onSuccess={() => {
          window.location.reload();
        }}
      />
    </div>
  );
};

export default MyRepairRequestsPage;

